import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, EmptyState, Skeleton } from '../../../../components/common';
import { IllustrationEmptyCart } from '../../../../components/svg';
import { formatPrice } from '../../../../utility/utility';
import { IconRemoveBundle } from './icons';
import { BundleOrderSummaryStyled } from './styled';

const BundleOrderSummary = ({
  cartData,
  isCartLoading,
  inError,
  refetch,
  isBusy,
  onRemove,
  onCheckout,
  readOnly = false,
  checkoutButtonText,
  proceedButtonLoading = false,
  disableProceedButton = false,
  hideCheckoutButton = false,
}) => {
  const { t } = useTranslation();
  const bundleLines = cartData?.bundleCartDetails ?? [];

  if (!cartData && isCartLoading) {
    return <Skeleton type="input" />;
  }

  if (inError) {
    return <EmptyState loading={isCartLoading} onClick={refetch} />;
  }

  if (!bundleLines.length) {
    return (
      <EmptyState
        illustration={<IllustrationEmptyCart />}
        className="empty-state-propshop"
        title={t('No Items')}
        message={t('Added in cart')}
        hideRetryButton
      />
    );
  }

  return (
    <BundleOrderSummaryStyled>
      <div className="bundle-summary-title">{t('Order Summary')}</div>
      {bundleLines.map((line) => (
        <div className="bundle-summary-line" key={line.id}>
          <div className="bundle-summary-line-name">
            {line.title}
            {line.quantity > 1 && <strong className="bundle-summary-line-quantity"> ({line.quantity})</strong>}
          </div>
          <div className="bundle-summary-line-price">
            Rs. {formatPrice(line.item_price ?? line.price)}
            {!readOnly && (
              <button type="button" disabled={isBusy} onClick={() => onRemove(line)}>
                <IconRemoveBundle />
              </button>
            )}
          </div>
        </div>
      ))}
      <div className="bundle-summary-totals">
        <div className="bundle-summary-total-row">
          <span>{t('Subtotal')}</span>
          <span>Rs. {formatPrice(cartData?.subtotal)}</span>
        </div>
        <div className="bundle-summary-total-row">
          <span>
            {cartData?.taxPercentage != null
              ? t('Tax ({{percentage}}%)', { percentage: cartData.taxPercentage })
              : t('Tax')}
          </span>
          <span>Rs. {formatPrice(cartData?.taxAmount)}</span>
        </div>
        <div className="bundle-summary-total-row bundle-summary-total-row--grand">
          <span>{t('Total')}</span>
          <span>Rs. {formatPrice(cartData?.amount ?? cartData?.total)}</span>
        </div>
      </div>
      {!hideCheckoutButton && (
        <Button
          type="primary"
          size="large"
          block
          className="mt-16"
          onClick={onCheckout}
          loading={proceedButtonLoading}
          disabled={disableProceedButton}
        >
          {checkoutButtonText ?? t('Proceed To Payment')}
        </Button>
      )}
    </BundleOrderSummaryStyled>
  );
};

export default BundleOrderSummary;
